import React from "react";
import { Link, useOutletContext } from "react-router-dom";

const LAST_UPDATED = "November 4, 2026";

function Section({ title, children }) {
  return (
    <section className="mb-8">
      <h2 className="text-lg font-semibold text-white mb-3">{title}</h2>
      <div className="text-gray-400 text-sm leading-relaxed space-y-3">{children}</div>
    </section>
  );
}

function EnSections() {
  return (
    <>
      <Section title="1. Who we are">
        <p>
          ShadowVault is a product of AfterLight Technologies Inc. ("AfterLight", "we", "us"). These
          Terms govern your use of ShadowVault (the "Service"). By creating an account you agree
          to these Terms.
        </p>
      </Section>

      <Section title="2. What the Service does">
        <p>
          ShadowVault lets you upload documents, which are encrypted and stored, and lets you
          designate "Legacy Contacts" who can be granted access to some or all of your documents
          if you stop checking in for a period of time you choose (the "Continuity Check-In").
        </p>
        <p>
          Each uploaded file's cryptographic hash is submitted to OpenTimestamps, which anchors it
          to the Bitcoin blockchain. This lets anyone later verify that a specific file existed and
          was unaltered as of a specific time — only the hash is submitted, never the file itself
          or its contents.
        </p>
      </Section>

      <Section title="3. Your account">
        <p>
          You must provide an accurate email address and phone number to register. You're
          responsible for keeping your login credentials and verification codes confidential, and
          for all activity under your account. Tell us immediately if you suspect unauthorized
          access.
        </p>
      </Section>

      <Section title="4. Legacy Contacts and the Continuity Check-In">
        <p>
          You control who your Legacy Contacts are and what they can access (all documents, or
          only specific categories). If you don't log in within your chosen check-in interval,
          access is released to your Legacy Contacts automatically, at the access level you
          assigned — this is not reversible once triggered, and there is no separate approval step
          beyond the interval you set. You are solely responsible for choosing an interval and
          Legacy Contacts appropriate to your situation.
        </p>
      </Section>

      <Section title="5. Acceptable use">
        <p>
          You agree not to use the Service to store or share content you don't have the legal right
          to store, to attempt to disrupt or gain unauthorized access to the Service, or to use the
          Service in a way that violates applicable law.
        </p>
      </Section>

      <Section title="6. Deletion is permanent">
        <p>
          Deleting a file is final. Once deleted, there is no trash, backup, or recovery path — the
          file's encryption key is destroyed and cannot be recovered by you, by us, or by anyone
          else. Delete only what you're certain you no longer need.
        </p>
      </Section>

      <Section title="7. Service availability">
        <p>
          We aim to keep the Service available but don't guarantee uninterrupted access. The
          Service is provided "as is" and "as available," without warranties of any kind, to the
          fullest extent permitted by law.
        </p>
      </Section>

      <Section title="8. Limitation of liability">
        <p>
          To the fullest extent permitted by law, AfterLight Technologies Inc. is not liable for
          indirect, incidental, or consequential damages arising from your use of the Service,
          including loss of access to documents due to a missed check-in, account compromise, or
          deletion.
        </p>
      </Section>

      <Section title="9. Changes to these Terms">
        <p>
          We may update these Terms from time to time. Continued use of the Service after a change
          takes effect means you accept the updated Terms.
        </p>
      </Section>

      <Section title="10. Contact">
        <p>
          Questions about these Terms can be sent through the 24/7 support chat in the app, or by
          email to support@shadowvault.ca.
        </p>
      </Section>

    </>
  );
}

function ZhSections() {
  return (
    <>
      <Section title="1. 我们是谁">
        <p>
          ShadowVault 是 AfterLight Technologies Inc.（下称"AfterLight"或"我们"）旗下的产品。本条款约束您对 ShadowVault（下称"本服务"）的使用。创建账户即表示您同意本条款。
        </p>
      </Section>

      <Section title="2. 本服务的功能">
        <p>
          ShadowVault 允许您上传文件（文件经加密后存储），并指定"传承联系人"。如果您在自行设定的时间内未进行确认（下称"定期确认"），传承联系人即可获得您部分或全部文件的访问权限。
        </p>
        <p>
          每个上传文件的加密哈希值都会提交至 OpenTimestamps，并锚定到比特币区块链。任何人日后都可以借此验证某个文件在特定时间已存在且未被篡改——提交的仅是哈希值，从不包括文件本身或其内容。
        </p>
      </Section>

      <Section title="3. 您的账户">
        <p>
          注册时您必须提供准确的邮箱地址和手机号码。您有责任对登录凭据和验证码保密，并对您账户下的所有活动负责。如怀疑账户遭到未经授权的访问，请立即通知我们。
        </p>
      </Section>

      <Section title="4. 传承联系人与定期确认">
        <p>
          您可以决定谁是您的传承联系人，以及他们可以访问哪些内容（全部文件或仅特定分类）。如果您未在设定的确认周期内登录，访问权限将按照您设定的权限级别自动释放给您的传承联系人——一旦触发即不可撤销，除您设定的周期外没有额外的审批步骤。您须自行负责选择适合自身情况的确认周期和传承联系人。
        </p>
      </Section>

      <Section title="5. 可接受的使用">
        <p>
          您同意不使用本服务存储或分享您无合法权利存储的内容，不试图干扰本服务或未经授权访问本服务，也不以违反适用法律的方式使用本服务。
        </p>
      </Section>

      <Section title="6. 删除是永久性的">
        <p>
          删除文件是最终操作。文件一经删除，即没有回收站、备份或恢复途径——文件的加密密钥会被销毁，您、我们或任何其他人都无法恢复。请仅删除您确定不再需要的文件。
        </p>
      </Section>

      <Section title="7. 服务可用性">
        <p>
          我们致力于保持本服务可用，但不保证访问不中断。在法律允许的最大范围内，本服务按"现状"和"现有"提供，不附带任何形式的保证。
        </p>
      </Section>

      <Section title="8. 责任限制">
        <p>
          在法律允许的最大范围内，AfterLight Technologies Inc. 对因您使用本服务而产生的任何间接、附带或后果性损害不承担责任，包括因错过定期确认、账户被盗用或文件删除而导致的文件无法访问。
        </p>
      </Section>

      <Section title="9. 本条款的变更">
        <p>
          我们可能会不时更新本条款。变更生效后继续使用本服务，即表示您接受更新后的条款。
        </p>
      </Section>

      <Section title="10. 联系我们">
        <p>
          如对本条款有任何疑问，请通过应用内的全天候支持聊天，或发送邮件至 support@shadowvault.ca 与我们联系。
        </p>
      </Section>
    </>
  );
}

export default function Terms() {
  const { lang } = useOutletContext();
  const zh = lang === "zh";
  return (
    <div className="w-full max-w-3xl mx-auto px-6 py-12">
      <h1 className="text-3xl font-bold text-white mb-2">{zh ? "服务条款" : "Terms of Service"}</h1>
      <p className="text-gray-500 text-sm mb-10">{zh ? "最后更新" : "Last updated"}: {LAST_UPDATED}</p>

      {zh ? (
        <>
          <p className="text-gray-400 text-sm mb-8 bg-dark-card border border-dark-border rounded-lg p-4">
            本中文版本仅供参考。如与英文版本有任何不一致，以英文版本为准。
          </p>
          <ZhSections />
        </>
      ) : (
        <EnSections />
      )}

      <div className="mt-12 text-center">
        <Link to="/" className="text-primary hover:text-primary-dark transition text-sm">
          {zh ? "← 返回 ShadowVault" : "← Back to ShadowVault"}
        </Link>
      </div>
    </div>
  );
}
